import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

interface DataSource {
  page: string;
  pagePath: string;
  icon: string;
  lastUpdated: string;
  sources: {
    name: string;
    url: string;
    description: string;
    dataTypes: string[];
  }[];
}

const dataSources: DataSource[] = [
  {
    page: 'Parks & Recreation',
    pagePath: '/parks',
    icon: 'ri-leaf-line',
    lastUpdated: 'January 15, 2025',
    sources: [
      {
        name: 'City of Roseville Parks & Recreation Department',
        url: 'https://www.roseville.ca.us/government/departments/parks_recreation',
        description: 'Official park counts, acreage, trail mileage, playground and sports facility inventories.',
        dataTypes: ['Park Counts', 'Trail Mileage', 'Facility Inventories', 'Acreage Data'],
      },
      {
        name: 'Roseville Parks Master Plan',
        url: 'https://www.roseville.ca.us',
        description: 'Long-range planning documents with comprehensive parkland data and future development plans.',
        dataTypes: ['Parkland Acreage', 'Development Plans'],
      },
    ],
  },
  {
    page: 'Events',
    pagePath: '/events',
    icon: 'ri-calendar-event-line',
    lastUpdated: 'March 20, 2025',
    sources: [
      {
        name: 'City of Roseville Parks & Recreation',
        url: 'https://www.roseville.ca.us/government/departments/parks_recreation',
        description: 'Official city-sponsored events including concerts, holiday celebrations, and community programs.',
        dataTypes: ['Event Dates', 'Locations', 'Admission Info', 'Event Descriptions'],
      },
      {
        name: 'Downtown Roseville Partnership',
        url: 'https://www.downtownroseville.com',
        description: 'Downtown events including Tuesday Nights, street festivals, and seasonal celebrations.',
        dataTypes: ['Street Festivals', 'Downtown Events', 'Vendor Information'],
      },
      {
        name: 'Placer County Fairgrounds',
        url: 'https://www.placercountyfair.org',
        description: 'County fair dates, schedules, and fairground event information.',
        dataTypes: ['Fair Dates', 'Event Schedules'],
      },
      {
        name: 'Verified Community Event Submissions',
        url: '/submit-business',
        description: 'Events submitted by local organizations and community members, verified before publication.',
        dataTypes: ['Community Events', 'Organization Events', 'Fundraisers'],
      },
    ],
  },
  {
    page: 'Community Calendar',
    pagePath: '/calendar',
    icon: 'ri-calendar-2-line',
    lastUpdated: 'March 22, 2025',
    sources: [
      {
        name: 'City of Roseville Parks & Recreation',
        url: 'https://www.roseville.ca.us/government/departments/parks_recreation',
        description: 'Official city events, recreation programs, and public facility schedules.',
        dataTypes: ['City Events', 'Recreation Programs', 'Facility Schedules'],
      },
      {
        name: 'Downtown Roseville Partnership',
        url: 'https://www.downtownroseville.com',
        description: 'Weekly and seasonal downtown events, markets, and cultural programming.',
        dataTypes: ['Downtown Events', 'Markets', 'Cultural Programs'],
      },
      {
        name: 'Roseville Arts Council',
        url: 'https://www.rosevillearts.org',
        description: 'Art exhibitions, gallery walks, workshops, and cultural events throughout Roseville.',
        dataTypes: ['Art Events', 'Workshops', 'Gallery Walks'],
      },
      {
        name: 'Verified Community Submissions',
        url: '/submit-business',
        description: 'Calendar events submitted by local businesses, nonprofits, and community groups.',
        dataTypes: ['Community Events', 'Nonprofit Events', 'Sports Events'],
      },
    ],
  },
  {
    page: 'Schools & Education',
    pagePath: '/schools',
    icon: 'ri-graduation-cap-line',
    lastUpdated: 'February 1, 2025',
    sources: [
      {
        name: 'Roseville City School District',
        url: 'https://www.rcsdk8.org',
        description: 'Elementary and middle school enrollment, ratings, test scores, and program information.',
        dataTypes: ['Enrollment', 'Test Scores', 'School Ratings', 'Programs'],
      },
      {
        name: 'Roseville Joint Union High School District',
        url: 'https://www.rjuhsd.us',
        description: 'High school data including enrollment, graduation rates, AP programs, and athletics.',
        dataTypes: ['Enrollment', 'Graduation Rates', 'AP Programs'],
      },
      {
        name: 'Dry Creek Joint Elementary School District',
        url: 'https://www.dcsdk12.org',
        description: 'Elementary and junior high school data for the Dry Creek district area.',
        dataTypes: ['Enrollment', 'Test Scores', 'Programs'],
      },
    ],
  },
  {
    page: 'Transportation',
    pagePath: '/transportation',
    icon: 'ri-bus-line',
    lastUpdated: 'January 20, 2025',
    sources: [
      {
        name: 'City of Roseville Public Works Department',
        url: 'https://www.roseville.ca.us/government/departments/public_works',
        description: 'Road infrastructure, parking facilities, bike lanes, and trail system data.',
        dataTypes: ['Trail Mileage', 'Parking Capacity', 'Bike Infrastructure'],
      },
      {
        name: 'Roseville Transit',
        url: 'https://www.roseville.ca.us/residents/transportation',
        description: 'Public transit routes, schedules, ridership data, and service area information.',
        dataTypes: ['Transit Routes', 'Schedules', 'Service Areas'],
      },
    ],
  },
  {
    page: 'Emergency Resources',
    pagePath: '/emergency',
    icon: 'ri-alarm-warning-line',
    lastUpdated: 'March 1, 2025',
    sources: [
      {
        name: 'Roseville Police Department',
        url: 'https://www.roseville.ca.us/government/departments/police',
        description: 'Non-emergency contact numbers, station locations, and community safety programs.',
        dataTypes: ['Contact Numbers', 'Station Locations'],
      },
      {
        name: 'Roseville Fire Department',
        url: 'https://www.roseville.ca.us/government/departments/fire',
        description: 'Fire station locations, emergency contact information, and fire safety resources.',
        dataTypes: ['Station Locations', 'Emergency Contacts'],
      },
      {
        name: 'Placer County Office of Emergency Services',
        url: 'https://www.placer.ca.gov/2981/Emergency-Services',
        description: 'County-wide emergency alerts, evacuation plans, and disaster preparedness resources.',
        dataTypes: ['Emergency Alerts', 'Evacuation Plans'],
      },
      {
        name: 'Sutter Roseville Medical Center',
        url: 'https://www.sutterhealth.org/find-location/facility/sutter-roseville-medical-center',
        description: 'Hospital location, emergency room information, and medical services.',
        dataTypes: ['Hospital Location', 'ER Information'],
      },
    ],
  },
  {
    page: 'Real Estate & Housing',
    pagePath: '/real-estate',
    icon: 'ri-home-4-line',
    lastUpdated: 'February 10, 2025',
    sources: [
      {
        name: 'Zillow',
        url: 'https://www.zillow.com/roseville-ca/',
        description: 'Median home prices, market trends, days on market, and active listing counts.',
        dataTypes: ['Median Prices', 'Market Trends', 'Listing Counts'],
      },
      {
        name: 'Realtor.com',
        url: 'https://www.realtor.com/realestateandhomes-search/Roseville_CA',
        description: 'Featured listings, neighborhood data, and home sale statistics.',
        dataTypes: ['Listings', 'Sale Statistics'],
      },
      {
        name: 'Walk Score\u00AE',
        url: 'https://www.walkscore.com/CA/Roseville',
        description: 'Walk Score and Transit Score ratings for Roseville neighborhoods.',
        dataTypes: ['Walk Scores', 'Transit Scores'],
      },
    ],
  },
  {
    page: 'Relocation Guide',
    pagePath: '/relocation',
    icon: 'ri-truck-line',
    lastUpdated: 'January 5, 2025',
    sources: [
      {
        name: 'U.S. Bureau of Labor Statistics',
        url: 'https://www.bls.gov',
        description: 'Cost of living index data and consumer price comparisons.',
        dataTypes: ['Cost of Living Index', 'Price Comparisons'],
      },
      {
        name: 'Numbeo',
        url: 'https://www.numbeo.com',
        description: 'Cost of living comparisons between cities including housing, groceries, and utilities.',
        dataTypes: ['City Comparisons', 'Living Costs'],
      },
      {
        name: 'City of Roseville Environmental Utilities',
        url: 'https://www.roseville.ca.us/government/departments/environmental_utilities',
        description: 'Water, wastewater, and solid waste utility provider information and rates.',
        dataTypes: ['Utility Rates', 'Provider Information'],
      },
      {
        name: 'Council for Community and Economic Research',
        url: 'https://www.c2er.org',
        description: 'Cost of living index methodology and regional economic data.',
        dataTypes: ['Economic Data', 'Cost Indices'],
      },
    ],
  },
  {
    page: 'Food & Drink',
    pagePath: '/food-drink',
    icon: 'ri-restaurant-line',
    lastUpdated: 'March 18, 2025',
    sources: [
      {
        name: 'Roseville Area Chamber of Commerce',
        url: 'https://www.rosevillechamber.com',
        description: 'Restaurant counts, business registrations, and local dining establishment data for the Roseville area.',
        dataTypes: ['Restaurant Counts', 'Business Registrations', 'Establishment Data'],
      },
      {
        name: 'Yelp',
        url: 'https://www.yelp.com/search?find_loc=Roseville%2C+CA',
        description: 'Restaurant ratings, review counts, cuisine categories, and pricing information.',
        dataTypes: ['Ratings', 'Reviews', 'Cuisine Categories', 'Price Ranges'],
      },
      {
        name: 'Participating Local Restaurants & Establishments',
        url: '/food-drink',
        description: 'Menu details, hours of operation, and special offerings submitted directly by restaurant owners.',
        dataTypes: ['Menus', 'Hours', 'Special Offers', 'Contact Details'],
      },
    ],
  },
  {
    page: 'Jobs & Employment',
    pagePath: '/jobs',
    icon: 'ri-briefcase-line',
    lastUpdated: 'March 5, 2025',
    sources: [
      {
        name: 'U.S. Bureau of Labor Statistics',
        url: 'https://www.bls.gov',
        description: 'Employment statistics, unemployment rates, average salary data, and labor market trends for the Sacramento-Roseville metro area.',
        dataTypes: ['Unemployment Rate', 'Average Salary', 'Labor Trends'],
      },
      {
        name: 'California Employment Development Department (EDD)',
        url: 'https://www.edd.ca.gov',
        description: 'State-level employment data, job growth projections, and industry-specific workforce statistics.',
        dataTypes: ['Job Growth', 'Industry Data', 'Workforce Statistics'],
      },
      {
        name: 'Roseville Area Chamber of Commerce',
        url: 'https://www.rosevillechamber.com',
        description: 'Top employer listings, Fortune 500 presence, and local business employment figures.',
        dataTypes: ['Top Employers', 'Employee Counts', 'Industry Sectors'],
      },
    ],
  },
  {
    page: 'Business Directory',
    pagePath: '/directory',
    icon: 'ri-store-2-line',
    lastUpdated: 'March 10, 2025',
    sources: [
      {
        name: 'Roseville.org Verified Business Submissions',
        url: '/submit-business',
        description: 'Business owners submit and verify their own listings including contact details, hours, and services.',
        dataTypes: ['Business Profiles', 'Contact Details', 'Hours of Operation', 'Services'],
      },
      {
        name: 'Roseville.org Community Reviews',
        url: '/directory',
        description: 'Customer ratings and reviews moderated for authenticity and quality.',
        dataTypes: ['Star Ratings', 'Written Reviews', 'Review Counts'],
      },
      {
        name: 'Placer County Business Registrations',
        url: 'https://www.placer.ca.gov',
        description: 'Business establishment dates and registration records for verification.',
        dataTypes: ['Establishment Dates', 'Registration Records'],
      },
    ],
  },
  {
    page: 'Deals & Coupons',
    pagePath: '/deals',
    icon: 'ri-coupon-3-line',
    lastUpdated: 'March 12, 2025',
    sources: [
      {
        name: 'Participating Roseville Businesses',
        url: '/deals',
        description: 'Deals, discounts, and coupon codes submitted directly by local business owners and verified before publication.',
        dataTypes: ['Deal Pricing', 'Discount Amounts', 'Coupon Codes', 'Expiration Dates'],
      },
      {
        name: 'Roseville.org Directory Cross-Reference',
        url: '/directory',
        description: 'Business details and ratings cross-referenced with our verified business directory.',
        dataTypes: ['Business Ratings', 'Business Profiles', 'Claim Counts'],
      },
    ],
  },
  {
    page: 'Neighborhoods',
    pagePath: '/neighborhoods',
    icon: 'ri-community-line',
    lastUpdated: 'February 15, 2025',
    sources: [
      {
        name: 'Walk Score®',
        url: 'https://www.walkscore.com',
        description: 'Walk Score and Transit Score ratings for individual neighborhoods.',
        dataTypes: ['Walk Scores', 'Transit Scores'],
      },
      {
        name: 'Roseville.org Business Directory',
        url: '/directory',
        description: 'Business counts, reviews, and dining options from verified directory listings.',
        dataTypes: ['Business Counts', 'Reviews', 'Dining Options'],
      },
      {
        name: 'Google Maps',
        url: 'https://maps.google.com',
        description: 'Distance measurements and location data for nearby attractions.',
        dataTypes: ['Distance Data', 'Location Information'],
      },
    ],
  },
  {
    page: 'Weather',
    pagePath: '/weather',
    icon: 'ri-sun-cloudy-line',
    lastUpdated: 'March 15, 2025',
    sources: [
      {
        name: 'National Weather Service — Sacramento',
        url: 'https://www.weather.gov/sto/',
        description: 'Official weather forecasts, alerts, and historical climate data for the Sacramento region.',
        dataTypes: ['Forecasts', 'Weather Alerts', 'Climate Data'],
      },
      {
        name: 'Weather.gov',
        url: 'https://www.weather.gov',
        description: 'National weather data, severe weather warnings, and forecast models.',
        dataTypes: ['Severe Warnings', 'Forecast Models'],
      },
    ],
  },
  {
    page: 'Emergency Preparedness',
    pagePath: '/preparedness',
    icon: 'ri-shield-check-line',
    lastUpdated: 'January 28, 2025',
    sources: [
      {
        name: 'Ready.gov',
        url: 'https://www.ready.gov',
        description: 'Federal emergency preparedness guidelines, checklists, and family planning resources.',
        dataTypes: ['Preparedness Checklists', 'Family Plans', 'Kit Guidelines'],
      },
      {
        name: 'Cal OES (California Governor\u2019s Office of Emergency Services)',
        url: 'https://www.caloes.ca.gov',
        description: 'California-specific emergency preparedness data, wildfire and earthquake readiness.',
        dataTypes: ['State Guidelines', 'Wildfire Preparedness', 'Earthquake Readiness'],
      },
      {
        name: 'Placer County Office of Emergency Services',
        url: 'https://www.placer.ca.gov/2981/Emergency-Services',
        description: 'Local emergency statistics, shelter locations, and community preparedness programs.',
        dataTypes: ['Local Statistics', 'Shelter Locations'],
      },
    ],
  },
];

const FORM_URL = 'https://readdy.ai/api/form/d6bp7hatehdqnvnpi3i0';

export default function DataSourcesPage() {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [formData, setFormData] = useState({ name: '', email: '', page: '', description: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name || !formData.email || !formData.page || !formData.description) {
      setFormStatus('error');
      return;
    }
    if (formData.description.length > 500) {
      setFormStatus('error');
      return;
    }

    setFormStatus('submitting');
    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('page', formData.page);
      body.append('description', formData.description);

      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', page: '', description: '' });
      } else {
        setFormStatus('error');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6">
            <i className="ri-database-2-line"></i>
            Transparency &amp; Accuracy
          </div>
          <h1 className="text-5xl font-bold mb-6">Data Sources &amp; Attributions</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto leading-relaxed">
            We believe in transparency. Here you will find every data source referenced across Roseville.org, along with
            links to verify the information yourself.
          </p>
        </div>
      </section>

      {/* Disclaimer Banner */}
      <section className="bg-amber-50 border-b border-amber-200">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-error-warning-line text-amber-600 text-xl"></i>
            </div>
            <div>
              <h2 className="text-sm font-bold text-amber-900 mb-1">Important Disclaimer</h2>
              <p className="text-sm text-amber-800 leading-relaxed">
                Statistics and data displayed on Roseville.org are gathered from the sources listed below and are
                provided for <strong>informational purposes only</strong>. While we strive for accuracy, data may change
                over time. We strongly encourage residents to{' '}
                <strong>verify all information directly with the official source</strong> before making decisions.
                Roseville.org is not responsible for inaccuracies in third-party data. If you find outdated or
                incorrect information, please{' '}
                <a href="#report-form" className="underline font-semibold hover:text-amber-950 transition-colors">
                  report it below
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-10 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-teal-600">{dataSources.length}</div>
              <div className="text-sm text-gray-600 mt-1">Pages with Data</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-600">
                {dataSources.reduce((acc, ds) => acc + ds.sources.length, 0)}
              </div>
              <div className="text-sm text-gray-600 mt-1">Verified Sources</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-600">2025</div>
              <div className="text-sm text-gray-600 mt-1">Last Reviewed</div>
            </div>
          </div>
        </div>
      </section>

      {/* Sources List */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Sources by Page</h2>
          <p className="text-sm text-gray-600 mb-10">Click any section to expand and view detailed source information.</p>

          <div className="space-y-4">
            {dataSources.map((ds) => {
              const isExpanded = expandedSection === ds.page;
              return (
                <div
                  key={ds.page}
                  className={`bg-white rounded-xl border transition-all ${
                    isExpanded ? 'border-teal-300 shadow-lg' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <button
                    onClick={() => setExpandedSection(isExpanded ? null : ds.page)}
                    className="w-full flex items-center justify-between px-6 py-5 cursor-pointer"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isExpanded ? 'bg-teal-100' : 'bg-gray-100'}`}>
                        <i className={`${ds.icon} text-lg ${isExpanded ? 'text-teal-600' : 'text-gray-500'}`}></i>
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold text-gray-900">{ds.page}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {ds.sources.length} source{ds.sources.length > 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Link
                        to={ds.pagePath}
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs text-teal-600 hover:text-teal-700 font-medium hidden sm:flex items-center gap-1 transition-colors"
                      >
                        Visit Page <i className="ri-arrow-right-up-line"></i>
                      </Link>
                      <span className="text-[11px] text-gray-400 hidden sm:flex items-center gap-1">
                        <i className="ri-time-line"></i>Updated: {ds.lastUpdated}
                      </span>
                      <i className={`ri-arrow-down-s-line text-xl text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}></i>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="px-6 pb-6 border-t border-gray-100 pt-4 space-y-4">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-time-line text-gray-400 text-sm"></i>
                        <span className="text-xs text-gray-500">Data last updated: <strong className="text-gray-700">{ds.lastUpdated}</strong></span>
                      </div>
                      {ds.sources.map((source) => (
                        <div key={source.name} className="bg-gray-50 rounded-lg p-5">
                          <div className="flex items-start justify-between gap-4 mb-2">
                            <h4 className="font-semibold text-gray-900 text-sm">{source.name}</h4>
                            <a
                              href={source.url}
                              target="_blank"
                              rel="noopener noreferrer nofollow"
                              className="text-xs text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1 whitespace-nowrap cursor-pointer transition-colors"
                            >
                              <i className="ri-external-link-line"></i>
                              Visit Source
                            </a>
                          </div>
                          <p className="text-xs text-gray-600 leading-relaxed mb-3">{source.description}</p>
                          <div className="flex flex-wrap gap-2">
                            {source.dataTypes.map((dt) => (
                              <span
                                key={dt}
                                className="bg-white text-gray-600 text-[11px] font-medium px-2.5 py-1 rounded-full border border-gray-200"
                              >
                                {dt}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Report Incorrect Data Form */}
      <section id="report-form" className="py-16 bg-gray-50">
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 text-teal-600 bg-teal-50 text-sm font-medium px-4 py-1.5 rounded-full mb-4">
              <i className="ri-feedback-line"></i>
              Help Us Improve
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Report a Data Correction</h2>
            <p className="text-sm text-gray-600 max-w-lg mx-auto">
              Found outdated or incorrect information on our site? Let us know and we will update it promptly.
            </p>
          </div>

          {formStatus === 'success' ? (
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-8 text-center">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-teal-900 mb-2">Thank You!</h3>
              <p className="text-sm text-teal-700">Your report has been submitted. We will review and update the data as needed.</p>
              <button
                onClick={() => setFormStatus('idle')}
                className="mt-4 text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap"
              >
                Submit Another Report
              </button>
            </div>
          ) : (
            <form
              id="data-correction-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm space-y-5"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Your Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Jane Smith"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="jane@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Which Page?</label>
                <select
                  name="page"
                  value={formData.page}
                  onChange={(e) => setFormData({ ...formData, page: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
                >
                  <option value="">Select a page...</option>
                  {dataSources.map((ds) => (
                    <option key={ds.page} value={ds.page}>
                      {ds.page}
                    </option>
                  ))}
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Describe the Issue
                  <span className="text-gray-400 font-normal ml-1">
                    ({formData.description.length}/500)
                  </span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setFormData({ ...formData, description: e.target.value });
                    }
                  }}
                  maxLength={500}
                  required
                  rows={4}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                  placeholder="Please describe what data is incorrect and what the correct information should be..."
                />
              </div>

              {formStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 flex items-center gap-2">
                  <i className="ri-error-warning-line"></i>
                  Something went wrong. Please try again.
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'submitting' || formData.description.length > 500}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white py-3 rounded-lg font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formStatus === 'submitting' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-send-plane-line"></i>
                    Submit Report
                  </span>
                )}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* General Disclaimer */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Our Commitment to Accuracy</h3>
          <p className="text-sm text-gray-600 leading-relaxed mb-6">
            Roseville.org is committed to providing accurate, up-to-date information for our community. All statistics,
            contact numbers, and data points are sourced from official government agencies, verified public records, and
            reputable third-party providers. Our editorial team reviews and updates data on a regular basis. However, we
            always recommend verifying critical information directly with the original source, especially for emergency
            contacts, utility rates, and real estate data.
          </p>
          <p className="text-xs text-gray-400">
            Last comprehensive data review: January 2025 &bull; Next scheduled review: July 2025
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
